import React from 'react';
import ConfirmationDialog from '@/components/ui/confirmation-dialog';
import { Button } from '@/components/ui/button';
import { Trophy, AlertTriangle } from 'lucide-react';

interface MatchCompletionDialogProps {
  trigger: React.ReactNode;
  onConfirm: () => void;
  onCancel?: () => void;
  matchName?: string;
  reason?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const MatchCompletionDialog: React.FC<MatchCompletionDialogProps> = ({
  trigger,
  onConfirm,
  onCancel,
  matchName = 'this match',
  reason = 'match completion',
  open,
  onOpenChange
}) => {
  const getReasonText = () => {
    switch (reason) {
      case 'overs_completed':
        return 'All overs have been completed';
      case 'wickets_completed':
        return 'All wickets have been taken';
      case 'target_achieved':
        return 'The target score has been achieved';
      default:
        return 'The match conditions have been met';
    }
  };

  return (
    <ConfirmationDialog
      trigger={trigger}
      open={open}
      onOpenChange={onOpenChange}
      title="Complete Match"
      description={
        <div className="space-y-3">
          <p>
            Are you sure you want to complete <strong>{matchName}</strong>?
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">This action cannot be undone!</p>
                <p className="text-amber-700">
                  Once completed, the match will be locked and no further scoring changes can be made.
                  All statistics will be finalized and the match will appear in completed matches.
                </p>
              </div>
            </div>
          </div>
          {reason && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4 text-blue-600" />
                <p className="text-sm text-blue-800">
                  <strong>Reason:</strong> {getReasonText()}
                </p>
              </div>
            </div>
          )}
        </div>
      }
      confirmText="Complete Match"
      cancelText="Cancel"
      variant="destructive"
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
};

export default MatchCompletionDialog;
